import {
  Box,
  Button,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale/fr';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { Business_Profile_Enum, Minimum_Order_Value_Type_Enum } from '@kheops/graphql';
import { useLocation, useNavigate } from 'react-router';
import useOpenable from '../hooks/useOpenable';
import ContractConditionsDialog from '../common/components/ContractConditionsDialog';
import { useUpdateContractMutation } from '../mutations/__generated__/updateContract.generated';
import ContractsUtils from '../common/utils/contracts.utils';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { ContractConditions } from '../chats/state';
import { currentContextAtom } from '../state';
import { ContractPreview } from '../hooks/useContracts';
import { ContractsAsBuyerDocument } from '../queries/__generated__/contractsAsBuyer.generated';
import { ContractsAsSupplierDocument } from '../queries/__generated__/contractsAsSupplier.generated';
import useCurrencyFormat from '../hooks/useCurrencyFormat';
import DeliveryTrucks from '../assets/images/delivery_trucks.png';
import TrustedSupplierIcon from '../assets/icons/trusted_icon.svg';
import Boxes from '../assets/images/boxes.png';
import CashRegister from '../assets/images/cash_register.png';
import PackageImage from '../assets/images/3d-icons/package.png';
import ClipboardImage from '../assets/images/3d-icons/clipboard.png';
import DiscountImage from '../assets/images/3d-icons/discount.png';
import { formatMinimumOrderValue } from '../common/utils/common.utils';
import { BasketDocument } from '../queries/__generated__/basket.generated';
import ContractInfoCard from './ContractInfoCard';
import { canEditContractConditionsAtom, isWaitingForSubCatalogsSyncAtom, refreshCompanyProductListAtom } from './state';
import PackagingPricesDetails from './prices/PackagingPricesDetails';
import { CompanyInfo } from './companyInfo/CompanyInfoCard';
import TrustedSupplierCard from './TrustedSupplierCard';
import ManageTrustedSupplierDialog from './ManageTrustedSupplierDialog';
import DeactivateTrustedSupplierConfirmDialog from './DeactivateTrustedSupplierConfirmDialog';

export interface ContractInfoCardsProps {
  contract: ContractPreview;
  company: CompanyInfo;
}

export default function ContractInfoCards({ contract, company }: ContractInfoCardsProps): React.JSX.Element {
  const { t } = useTranslation(['catalogs', 'contracts', 'common']);
  const { realm } = useAtomValue(currentContextAtom);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const { open: openContractConditionsDialog, isOpen: isContractConditionsDialogOpen, close } = useOpenable();
  const { open: openPackagingDetails, isOpen: isPackagingDetailsOpen, close: closePackagingDetails } = useOpenable();
  const { open: openManageTrustedSupplierDialog, isOpen: isManageTrustedSupplierDialogOpen, close: closeManageTrustedSupplierDialog } = useOpenable();
  const { open: openDeactivateTrustedSupplierDialog, isOpen: isDeactivateTrustedSupplierDialogOpen, close: closeDeactivateTrustedSupplierDialog } = useOpenable();
  const [updateContract, { data }] = useUpdateContractMutation({
    refetchQueries: [ContractsAsBuyerDocument, ContractsAsSupplierDocument, BasketDocument],
    awaitRefetchQueries: true,
  });
  const setRefresh = useSetAtom(refreshCompanyProductListAtom);
  const isWaitingForSubCatalogsSync = useAtomValue(isWaitingForSubCatalogsSyncAtom);
  const [loading, setLoading] = useState(false);
  const [subCatalogChanged, setSubCatalogChanged] = useState(false);
  const canEditContractConditions = useAtomValue(canEditContractConditionsAtom);
  const shouldDisplayTrustedSupplierCard = realm === Business_Profile_Enum.Buyer && contract.is_trusted_supplier;
  const location = useLocation();
  const navigate = useNavigate();

  const handleContractUpdate = useCallback((response?: Partial<ContractConditions>) => {
    close();

    if (response) {
      setLoading(true);

      setSubCatalogChanged(response.sub_catalog_id !== contract.sub_catalog_id);

      updateContract({
        variables: {
          contractId: contract.id,
          contractInput: {
            discount: response.discount,
            minimum_order_value: response.minimum_order_value || 0,
            minimum_order_value_type: response.minimum_order_value_type || Minimum_Order_Value_Type_Enum.Price,
            custom_price_list_id: response.custom_price_list_id,
            sub_catalog_id: response.sub_catalog_id,
          },
        },
      });
    }
  }, [contract]);

  const currencyFormatter = useCurrencyFormat({ maximumFractionDigits: 0 });

  useEffect((): (() => void) | void => {
    const displaySuccessSnackbar = (): void => {
      setCommonSnackbarProps({
        label: t('common:update_successful'),
        snackbarProps: {
          open: true,
        },
      });

      setLoading(false);
    };

    if (data?.update_contract_by_pk?.id) {
      if (subCatalogChanged) {
        const timeout = setTimeout(
          () => {
            displaySuccessSnackbar();
            setRefresh(true);
          },
          4000, // We need to wait for the indexation of packagings records. 4s corresponds to a high waiting time for a company with over 200 products.
        );

        return () => clearTimeout(timeout);
      }

      displaySuccessSnackbar();
    }
  }, [data]);

  useEffect(() => {
    if (location.state?.newSubCatalogId || location.state?.newCustomPriceListId) {
      openContractConditionsDialog();
      // to prevent dialog form opening again after changing tabs
      navigate('.', { replace: true });
    }
  }, []);

  const shouldDisplayPricesDetailsButton = realm === Business_Profile_Enum.Buyer && (!!contract.custom_price_list_id || !!contract.discount);
  const shouldDisplayTrustedSupplierActivation = realm === Business_Profile_Enum.Buyer && !contract.is_trusted_supplier && canEditContractConditions;

  const handleCloseManageTrustedSupplierDialog = (confirm: boolean): void => {
    if (confirm) {
      closeManageTrustedSupplierDialog();
      openDeactivateTrustedSupplierDialog();
      return;
    }
    closeManageTrustedSupplierDialog();
  };

  return (
    <Box sx={{ mx: 2 }}>
      <DeactivateTrustedSupplierConfirmDialog
        open={isDeactivateTrustedSupplierDialogOpen}
        onClose={closeDeactivateTrustedSupplierDialog}
        contract={contract}
      />
      <ManageTrustedSupplierDialog
        contract={contract}
        onClose={handleCloseManageTrustedSupplierDialog}
        open={isManageTrustedSupplierDialogOpen}
      />
      {shouldDisplayTrustedSupplierActivation && (
        <TrustedSupplierCard contract={contract} />
      )}
      <Typography variant="titleLarge" sx={{ mt: 3, mb: 1.5 }}>{t('common:general_information')}</Typography>
      <Box
        sx={{
          backgroundColor: 'surfaceContainerLow.main',
          borderRadius: 6,
          p: 2,
          width: {
            lg: 'fit-content',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: {
              xs: 'column',
              sm: shouldDisplayTrustedSupplierCard && ContractsUtils.displayPriceMode(realm!, contract) ? 'column' : 'row',
              md: 'row',
            },
            gap: 1.5,
          }}
        >
          <ContractInfoCard
            imageUrl={PackageImage}
            imageWidth={40}
            imageHeight={40}
            title={formatMinimumOrderValue(t, contract.minimum_order_value || 0, contract.minimum_order_value_type)}
            text={t('common:minimum_order_value')}
            containerColor="surfaceContainerHigh.main"
            titleVariant="bodyLarge"
          />
          {ContractsUtils.displayPriceMode(realm!, contract) && (
            <ContractInfoCard
              imageUrl={DiscountImage}
              imageWidth={40}
              imageHeight={40}
              title={ContractsUtils.getPriceModeLabel(t, realm, contract.discount, contract.custom_price_list?.name)}
              text={t(contract.discount || contract.custom_price_list ? 'contracts:discount' : 'common:prices')}
              containerColor="surfaceContainerHigh.main"
              titleVariant="bodyLarge"
            >
              {shouldDisplayPricesDetailsButton && (
                <>
                  <Button
                    variant="tonal"
                    onClick={openPackagingDetails}
                    sx={{ mt: 1.5 }}
                  >
                    {t('price:see_the_prices')}
                  </Button>
                  <PackagingPricesDetails
                    open={isPackagingDetailsOpen}
                    onClose={closePackagingDetails}
                    customPriceListId={contract.custom_price_list_id}
                    subCatalogId={contract.sub_catalog_id}
                    companyId={company.id}
                    globalDiscount={contract.discount}
                  />
                </>
              )}
            </ContractInfoCard>
          )}
          {realm === Business_Profile_Enum.Supplier && (
            <ContractInfoCard
              imageUrl={ClipboardImage}
              imageWidth={40}
              imageHeight={40}
              title={contract.sub_catalog?.name || t('catalogs:default_catalog')}
              text={t('catalogs:catalog')}
              containerColor="surfaceContainerHigh.main"
              titleVariant="bodyLarge"
            />
          )}
          {shouldDisplayTrustedSupplierCard && (
            <ContractInfoCard
              imageUrl={TrustedSupplierIcon}
              title={t('contracts:trusted_partner')}
              text={t('contracts:auto_validated_order_propositions')}
              imageHeight={40}
              containerColor="surfaceContainerHigh.main"
              titleVariant="bodyLarge"
            >
              {canEditContractConditions && (
                <Button
                  variant="tonal"
                  sx={{ mt: 1.5 }}
                  onClick={openManageTrustedSupplierDialog}
                >
                  {t('common:manage')}
                </Button>
              )}
            </ContractInfoCard>
          )}
        </Box>
        {realm === Business_Profile_Enum.Supplier && (
          <Box
            sx={{
              mt: 2,
              textAlign: 'end',
            }}
          >
            <Button
              variant="tonal"
              startIcon={<EditIcon />}
              onClick={openContractConditionsDialog}
              loading={loading || isWaitingForSubCatalogsSync}
            >
              {t('common:update')}
            </Button>
            {isContractConditionsDialogOpen && (
              <ContractConditionsDialog
                open={isContractConditionsDialogOpen}
                onClose={handleContractUpdate}
                title={t('contracts:update_your_agreement')}
                description={t('contracts:update_your_agreement_description', { company: company.tradeName })}
                contractId={contract.id}
                conditions={{
                  supplyingCompanyId: contract.supplyingCompanyId,
                  discount: contract.discount,
                  minimum_order_value: contract.minimum_order_value,
                  minimum_order_value_type: contract.minimum_order_value_type,
                  custom_price_list_id: contract.custom_price_list_id,
                  sub_catalog_id: contract.sub_catalog_id,
                }}
                commissionRate={contract.supplier_commission_rate as number}
                submitLabel={t('common:validate')}
              />
            )}
          </Box>
        )}
      </Box>

      <Typography variant="titleLarge" sx={{ mt: 3, mb: 1.5 }}>
        {t('common:statistics')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {
            xs: 'column',
            sm: 'row',
          },
          gap: 1.5,
          flex: 1,
          maxWidth: {
            lg: 824,
          },
        }}
      >
        <ContractInfoCard
          imageUrl={CashRegister}
          title={currencyFormatter.format(contract?.total_revenue || 0)}
          text={t('contracts:total_purchase_excluding_tax')}
          imageHeight={48}
          containerColor="surfaceContainerLow.main"
        />
        <ContractInfoCard
          imageUrl={Boxes}
          title={contract.order_count || 0}
          text={t('contracts:orders_count')}
          imageHeight={48}
          containerColor="surfaceContainerLow.main"
        />
        <ContractInfoCard
          imageUrl={DeliveryTrucks}
          title={contract.last_order_date ? formatDistanceToNow(contract.last_order_date, { addSuffix: true, locale: fr }) : '-'}
          text={t('contracts:last_order')}
          imageHeight={48}
          containerColor="surfaceContainerLow.main"
        />
      </Box>
    </Box>
  );
}
